import { statusRows } from '../statuses/expiry';
import { readItemField } from './item-field-reader';
import { sameJson } from './json-change';
/** Paths are relative to `.computed`; root mirrors carry the mutable derived
 * fields outside that bucket (including charge entries). Empty patches cover rows. */
export interface ComputedEntityDelta {
    set: Array<{ path: string[]; value: unknown }>;
    unset: string[][];
    before?: Array<{ path: string[]; value?: unknown; absent?: true }>;
    mirrors?: ComputedEntityDelta;
    statuses?: { target?: string[]; caster?: string[] } | null;
    /** Exact display projection; saved status rows travel separately in frame.rows. */
    status_projection?: unknown;
    /** Saved rows are authoritative even when a derived cluster could not project. */
    status_projection_failure?: string;
}
export type ActionComputedDelta = Record<string, ComputedEntityDelta>;
export type ComputedMember = readonly [id: string, row: Record<string, unknown>];

/** Mutable paths are witnessed deeply; replacement paths borrow the old immutable
 * branch and compare only changed identities. Writers declare these before apply. */
export interface ComputedWritePlan {
    computed?: readonly (readonly string[])[];
    mirrors?: readonly (readonly string[])[];
    replaceComputed?: readonly (readonly string[])[];
    replaceMirrors?: readonly (readonly string[])[];
    statuses?: boolean;
}
type ObservationWork = { planned: number; unplanned: number; objects: number; entries: number };
const page = globalThis as { ftComputedWriteWork?: ObservationWork; ftComputedWriteMetrics?: () => ObservationWork };
const work = page.ftComputedWriteWork ??= { planned: 0, unplanned: 0, objects: 0, entries: 0 };
page.ftComputedWriteMetrics = () => ({ ...work });

/** A writer which only changes saved rows declares that fact before applying it. */
export function unchangedActionComputed(members: Iterable<ComputedMember>): ActionComputedDelta {
    try {
        const out: ActionComputedDelta = {};
        for (const [id, row] of members) {
            if (row._action_computed_unavailable === true) { delete row._action_computed_unavailable; continue; }
            out[id.toUpperCase()] = { set: [], unset: [] };
        }
        return out;
    } catch { return {}; }
}
type Branch = Record<string, unknown>;
const branch = (value: unknown): value is Branch => value !== null && typeof value === 'object';
const hasOwn = (value: object, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);
const saved = new Set(['computed', 'catalog', 'encounter_creatures', 'encounters', 'player_characters', 'items',
    'statuses', 'position', 'inventory', 'inventory_in_container', 'encounter_creatures_arr', 'encounter_inventories_arr', 'permissions', 'viewer']);
const mirrors = (row: Branch): Branch => Object.fromEntries(Object.entries(row).filter(([key]) => !key.startsWith('_') && !saved.has(key)));
interface Observation { values: Map<string, unknown>; length?: number; depth?: number }
export interface ComputedWriteObservation {
    (): ActionComputedDelta;
    recordComputedWrites(memberId: string, paths: readonly (readonly string[])[]): void;
}

/** Retain scalar values and branch identities across the synchronous apply.
 * Writers keep their plain held objects; only emitted paths get before-images.
 * This is a change witness, with no restore/history operation. */
export function observeActionComputed(members: Iterable<ComputedMember>, plans?: ReadonlyMap<string, ComputedWritePlan>): ComputedWriteObservation {
    type Write = { path: string[]; present: boolean; value: unknown };
    const roots = new Map<string, { row: Branch; present: boolean; value: unknown; mirrors?: Branch; status: unknown;
        computedWrites?: Write[]; mirrorWrites?: Write[]; statuses?: boolean }>();
    let observed = new WeakMap<object, Observation>();
    function observe(value: unknown, depth = Infinity): void {
        if (!depth || !branch(value)) return;
        const existing = observed.get(value);
        if (existing) {
            if ((existing.depth ?? Infinity) >= depth) return;
            existing.depth = depth;
            for (const child of existing.values.values()) observe(child, depth - 1);
            return;
        }
        const values = new Map(Object.entries(value));
        work.objects++; work.entries += values.size;
        observed.set(value, { values, depth, ...(Array.isArray(value) ? { length: value.length } : {}) });
        for (const child of values.values()) observe(child, depth - 1);
    }
    function read(value: unknown, path: readonly string[]): { present: boolean; value: unknown } {
        for (const key of path) {
            if (!branch(value) || !hasOwn(value, key)) return { present: false, value: undefined };
            value = value[key];
        }
        return { present: path.length > 0 || value !== undefined, value };
    }
    function writes(value: unknown, mutable: ComputedWritePlan['computed'] = [], replacements: ComputedWritePlan['computed'] = []): Write[] {
        const out: Write[] = [];
        for (const [paths, deep] of [[mutable, true], [replacements, false]] as const) for (const path of paths) {
            // A newly created parent must be emitted at its first missing ancestor.
            const effective: string[] = [];
            for (const key of path) {
                const parent = read(value, effective).value;
                if (!branch(parent)) break;
                observe(parent, 1);
                effective.push(key); if (!read(value, effective).present) break;
            }
            const prior = read(value, effective);
            if (deep) observe(prior.value);
            if (out.some(write => write.path.length <= effective.length && write.path.every((key, i) => effective[i] === key))) continue;
            for (let i = out.length - 1; i >= 0; i--) if (effective.every((key, j) => out[i]!.path[j] === key)) out.splice(i, 1);
            out.push({ path: effective, ...prior });
        }
        return out;
    }
    for (const [id, row] of members) {
        const key = id.toUpperCase(), plan = plans?.get(key);
        const root = { row, present: hasOwn(row, 'computed'), value: row.computed, status: row.statuses };
        if (plans) {
            if (!plan) throw new Error(`computed-write-plan-missing:${key}`);
            work.planned++;
            roots.set(key, { ...root, computedWrites: writes(root.value, plan.computed, plan.replaceComputed),
                mirrorWrites: writes(row, plan.mirrors, plan.replaceMirrors), statuses: plan.statuses });
            // Status writers replace entries; only the two list containers mutate.
            // Embedded status definitions are borrowed, never indexed as before-images.
            if (plan.statuses) observe(root.status, 2);
        } else {
            // Remaining inventory/PL/combat writers have not supplied a write plan.
            work.unplanned++;
            const extra = mirrors(row);
            roots.set(key, { ...root, mirrors: extra });
            observe(root.value); observe(extra); observe(root.status);
        }
    }
    function before(value: unknown): unknown {
        if (!branch(value)) return value;
        const prior = observed.get(value);
        if (!prior) return value;
        const copy: Branch = Array.isArray(value) ? new Array(prior.length) as unknown as Branch : {};
        for (const [key, child] of prior.values) Object.defineProperty(copy, key,
            { value: before(child), writable: true, configurable: true, enumerable: true });
        return copy;
    }
    function patch(old: unknown, next: unknown, present = true, exists = true, witness = true, at: readonly string[] = []): ComputedEntityDelta {
        const out: ComputedEntityDelta = { set: [], unset: [], before: [] }, path = [...at];
        function emit(old: unknown, next: unknown, present: boolean, exists: boolean): void {
            if (witness) out.before!.push({ path: path.slice(), ...(present ? { value: before(old) } : { absent: true }) });
            if (exists) out.set.push({ path: path.slice(), value: next }); else out.unset.push(path.slice());
        }
        function diff(old: unknown, next: unknown): void {
            if (branch(old) && branch(next) && Array.isArray(old) === Array.isArray(next)) {
                let prior = observed.get(old);
                // Undeclared descendants of replacement branches are immutable.
                // Shared identities need no visit, including large held catalogs.
                if (!prior) {
                    if (Object.is(old, next)) return;
                    prior = { values: new Map(Object.entries(old)), ...(Array.isArray(old) ? { length: old.length } : {}) };
                }
                const keys = Object.keys(next), order = [...prior.values.keys()].filter(key => hasOwn(next, key));
                order.push(...keys.filter(key => !prior.values.has(key)));
                if ((Array.isArray(next) && prior.length !== next.length) || keys.some((key, i) => key !== order[i])) {
                    emit(old, next, true, true); return;
                }
                for (const key of prior.values.keys()) if (!hasOwn(next, key)) {
                    path.push(key); emit(prior.values.get(key), undefined, true, false); path.pop();
                }
                for (const key of keys) {
                    path.push(key);
                    if (prior.values.has(key)) diff(prior.values.get(key), next[key]);
                    else emit(undefined, next[key], false, true);
                    path.pop();
                }
            } else if (!Object.is(old, next)) emit(old, next, true, true);
        }
        if (present !== exists) emit(old, next, present, exists); else if (exists) diff(old, next);
        if (!out.before!.length) delete out.before;
        return out;
    }
    let finished: ActionComputedDelta | undefined;
    const finish = (() => {
        if (finished) return finished;
        const delta: ActionComputedDelta = {};
        let changed = false;
        try {
            for (const [id, root] of roots) {
                const { row } = root;
                if (row._action_computed_unavailable === true) { delete row._action_computed_unavailable; continue; }
                const scopedPatch = (writes: Write[], next: unknown): ComputedEntityDelta => {
                    const out: ComputedEntityDelta = { set: [], unset: [] };
                    for (const write of writes) {
                        const current = read(next, write.path);
                        const delta = patch(write.value, current.value, write.present, current.present, true, write.path);
                        out.set.push(...delta.set); out.unset.push(...delta.unset);
                        if (delta.before) (out.before ??= []).push(...delta.before);
                    }
                    return out;
                };
                const out = root.computedWrites ? scopedPatch(root.computedWrites, row.computed)
                    : patch(root.value, row.computed, root.present, hasOwn(row, 'computed'));
                if (typeof row._action_status_projection_failure === 'string') {
                    out.status_projection_failure = row._action_status_projection_failure;
                    delete row._action_status_projection_failure;
                }
                const extra = root.mirrorWrites ? scopedPatch(root.mirrorWrites, row) : patch(root.mirrors, mirrors(row));
                if (extra.set.length || extra.unset.length) out.mirrors = extra;
                const status = root.computedWrites && !root.statuses ? { set: [], unset: [] } : patch(root.status, row.statuses, true, true, false);
                if (status.set.length || status.unset.length) {
                    const statuses = row.statuses;
                    out.status_projection = statuses ?? null;
                    out.statuses = branch(statuses) ? Object.fromEntries(['target', 'caster'].filter(side => hasOwn(statuses, side))
                        .map(side => [side, statusRows(row, side as 'target' | 'caster').map(status => String(readItemField(status, 'status_id') ?? '').toUpperCase())])) : null;
                }
                changed ||= out.set.length > 0 || out.unset.length > 0 || !!out.mirrors || hasOwn(out, 'status_projection') || !!out.status_projection_failure;
                delta[id] = out;
            }
            // Detach only the emitted wire payload, never a held member/tree.
            finished = changed ? JSON.parse(JSON.stringify(delta)) as ActionComputedDelta : delta;
            return finished;
        } catch { finished = {}; return finished; }
        finally { roots.clear(); observed = new WeakMap(); }
    }) as ComputedWriteObservation;
    // Record any additional declared path before its writer runs, including aliases.
    finish.recordComputedWrites = (memberId, paths) => {
        const root = roots.get(memberId.toUpperCase());
        if (finished || !root) return;
        if (root.computedWrites) {
            for (const write of writes(root.value, paths)) {
                if (root.computedWrites.some(prior => prior.path.length <= write.path.length && prior.path.every((key, i) => write.path[i] === key))) continue;
                root.computedWrites = root.computedWrites.filter(prior => !write.path.every((key, i) => prior.path[i] === key));
                root.computedWrites.push(write);
            }
            return;
        }
        for (const path of paths) {
            let value = root.value;
            for (const key of path) value = branch(value) ? value[key] : undefined;
            observe(value);
        }
    };
    return finish;
}

/** Copy only changed ancestors. Missing parents / divergent before-values recover
 * the entity; they never throw or partially edit its currently painted tree. */
export function applyComputedDelta(value: unknown, patch: ComputedEntityDelta): { ok: true; value: unknown } | { ok: false } {
    const read = (path: readonly string[]): { present: boolean; value: unknown } => {
        let node = value;
        for (const key of path) {
            if (!branch(node) || !hasOwn(node, key)) return { present: false, value: undefined };
            node = node[key];
        }
        return { present: path.length ? true : value !== undefined, value: node };
    };
    for (const expected of patch.before ?? []) {
        const actual = read(expected.path);
        if (expected.absent ? actual.present : !actual.present || !sameJson(actual.value, expected.value)) return { ok: false };
    }
    let next = value;
    function write(node: unknown, path: readonly string[], value: unknown, remove: boolean): { ok: true; value: unknown } | { ok: false } {
        if (!path.length) return { ok: true, value: remove ? undefined : sameJson(node, value) ? node : value };
        if (!branch(node)) return { ok: false };
        const [key, ...rest] = path;
        if (rest.length && !hasOwn(node, key!)) return { ok: false };
        const child = write(node[key!], rest, value, remove);
        if (!child.ok) return child;
        if (remove && !rest.length ? !hasOwn(node, key!) : hasOwn(node, key!) && child.value === node[key!]) return { ok: true, value: node };
        const copy = Array.isArray(node) ? node.slice() : { ...node };
        if (remove && !rest.length) delete (copy as Branch)[key!];
        else Object.defineProperty(copy, key!, { value: child.value, enumerable: true, configurable: true, writable: true });
        return { ok: true, value: copy };
    }
    for (const path of patch.unset) { const result = write(next, path, undefined, true); if (!result.ok) return result; next = result.value; }
    for (const set of patch.set) { const result = write(next, set.path, set.value, false); if (!result.ok) return result; next = result.value; }
    return { ok: true, value: next };
}
